import type { CatalogBrandItemDTO } from '@today/types'
import { Badge, Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/**
 * Props for {@link PlatformBrandSwitcherView}.
 *
 * @property options - The brand catalogue; only `selectable` brands can be chosen.
 * @property currentId - The platform brand the pointer names now; marked in the list.
 * @property selectedId - The brand chosen in the list (`''` = none yet).
 * @property onSelect - Called with the chosen brand id.
 * @property onSave - Called when the user saves the chosen brand.
 * @property saving - True while a save is in flight; disables Save.
 * @property error - A refused save's message, shown inline.
 * @property notice - A saved-with-a-caveat message (a pending propagation), shown as a warning.
 */
export interface PlatformBrandSwitcherViewProps {
	options: readonly CatalogBrandItemDTO[]
	currentId: string
	selectedId: string
	onSelect: (id: string) => void
	onSave: () => void
	saving: boolean
	error?: string | null
	notice?: string | null
}

/** One catalogue brand as a radio row, marked when it is the current platform brand. */
function BrandOption({
	option,
	current,
	checked,
	onSelect,
}: {
	option: CatalogBrandItemDTO
	current: boolean
	checked: boolean
	onSelect: (id: string) => void
}): ReactElement {
	return (
		<label className="flex items-center gap-3 rounded-ds-md border border-ds-border bg-ds-surface px-3 py-2 text-ds-text">
			<input
				type="radio"
				name="platform-brand"
				value={option.id}
				checked={checked}
				disabled={!option.selectable}
				onChange={() => onSelect(option.id)}
			/>
			<span className="flex-1">{option.name}</span>
			{current ? (
				<Badge variant="success" size="sm">
					current
				</Badge>
			) : null}
			{option.selectable ? null : (
				<Badge variant="muted" size="sm">
					{option.status}
				</Badge>
			)}
		</label>
	)
}

/**
 * The Super-Admin platform-brand switcher (TO-541): the catalogue as a radio list
 * with the current platform brand marked, deprecated brands listed but disabled,
 * and Save. Pure: the catalogue, the selection and the callbacks are props.
 */
export function PlatformBrandSwitcherView({
	options,
	currentId,
	selectedId,
	onSelect,
	onSave,
	saving,
	error,
	notice,
}: PlatformBrandSwitcherViewProps): ReactElement {
	return (
		<section className="flex max-w-xl flex-col gap-6">
			<fieldset className="flex flex-col gap-2">
				<legend className="mb-2 text-ds-base font-medium text-ds-text">Platform brand</legend>
				{options.map((option) => (
					<BrandOption
						key={option.id}
						option={option}
						current={option.id === currentId}
						checked={selectedId === option.id}
						onSelect={onSelect}
					/>
				))}
			</fieldset>
			{error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{error}
				</p>
			) : null}
			{notice ? (
				<p role="status" className="rounded-ds-md bg-ds-warning/10 px-3 py-2 text-ds-sm text-ds-text">
					{notice}
				</p>
			) : null}
			<div>
				<Button type="button" onPress={onSave} isDisabled={saving || selectedId === '' || selectedId === currentId}>
					Save
				</Button>
			</div>
		</section>
	)
}
